import { Directive, ElementRef, Input, Renderer2, OnChanges, SimpleChanges, HostListener, OnDestroy } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
  selector: '[appFormatDecimal]'
})
export class FormatDecimalDirective implements OnChanges, OnDestroy {
  @Input('appFormatDecimal') value: any; 

  private timeoutId: any;
  private isBlurring: boolean  = false;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private ngControl: NgControl 
  ) {}

  
  ngOnInit(): void {
    this.formatAndSetInputValue(this.value);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] && !this.isBlurring) {
      this.formatAndSetInputValue(changes['value'].currentValue);
    }
  }

  ngOnDestroy(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    this.isBlurring = true;
    const inputElement = this.el.nativeElement as HTMLInputElement;
    const currentValue = inputElement.value.trim();

    // If the value is empty after deletion, keep it empty
    if (!currentValue) {
      if (this.ngControl && this.ngControl.control) {
        this.ngControl.control.setValue('', { emitEvent: false });
      }
      return;
    }

    const cursorPosition = inputElement.selectionStart;
    const originalValue = inputElement.value;
    const formattedValue = this.formatPartialValue(currentValue);

    this.timeoutId = setTimeout(() => {
      if (!inputElement.isConnected) {
        return;
      }

      if (formattedValue !== inputElement.value) {
        this.renderer.setProperty(inputElement, 'value', formattedValue);
        this.restoreCursorPosition(inputElement, originalValue, formattedValue, cursorPosition);
      }

      if (this.ngControl && this.ngControl.control) {
        this.ngControl.control.setValue(formattedValue, { emitEvent: false });
      }
    });
  }

  @HostListener('blur')
  onBlur(): void {
    const inputElement = this.el.nativeElement as HTMLInputElement;
    const currentValue = inputElement.value.trim();

    const formattedValue = this.formatValue(currentValue);

    this.timeoutId = setTimeout(() => {
      if (!inputElement.isConnected) {
        return;
      }

      if (formattedValue !== inputElement.value) {
        this.renderer.setProperty(inputElement, 'value', formattedValue);

        inputElement.setSelectionRange(formattedValue.length, formattedValue.length);
      }

      if (this.ngControl && this.ngControl.control) {
        this.ngControl.control.setValue(formattedValue, { emitEvent: false }); // Prevent infinite loops
      }
      this.isBlurring = false;
    });
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const inputElement = this.el.nativeElement as HTMLInputElement;
    const rawValue = inputElement.value.trim();
    const hasDecimalPoint = rawValue.includes('.');
    const cursorPosition = inputElement.selectionStart;
    const selectionEnd = inputElement.selectionEnd;
    const isAllSelected = cursorPosition === 0 && selectionEnd === rawValue.length;
    
    // If all text is selected and user types a valid character, allow it
    if (isAllSelected) {
      if (event.key === '.') {
        return;
      }
    }

    const currentValue = rawValue; // Use raw value directly
    const decimalPointIndex = currentValue.indexOf('.');

    if (event.key === 'Backspace') {
      // Allow natural backspace behavior - don't interfere with cursor positioning
      return;
    } else {
      const isUtilityKey = [
        'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
        'Backspace', 'Delete', 'Tab', 'End', 'Home', 'Escape',
        'PageUp', 'PageDown', 'Insert', 'Enter', 'Shift', 'Control'
      ].includes(event.key);

      if (isUtilityKey) {
        return;
      }

      if ((event.ctrlKey || event.metaKey) && ['c', 'v', 'x', 'a'].includes(event.key.toLowerCase())) {
        return;
      }

      if (!/[\d.]/.test(event.key)) {
        event.preventDefault();
        return;
      }

      if (event.key === '-') {
        event.preventDefault();
        return;
      }

      // Only prevent decimal point if it already exists and text is not fully selected
      if (event.key === '.' && decimalPointIndex !== -1 && !isAllSelected) {
        event.preventDefault();
        return;
      }

      // Prevent typing more than 2 decimal places only if cursor is at the very end
      if (/\d/.test(event.key) && hasDecimalPoint && !isAllSelected && cursorPosition !== null) {
        const decimalPart = rawValue.split('.')[1] || '';
        // Only prevent if there are already 2 decimal digits AND cursor is at the very end of the decimal part
        if (decimalPart.length >= 2 && cursorPosition >= decimalPointIndex + decimalPart.length + 1) {
          event.preventDefault();
          return;
        }
      }

      // Allow 0 at start if followed by decimal point
      if (event.key === '0' && cursorPosition === 0) {
        if (currentValue.length > 0 && currentValue[0] === '0' && currentValue[1] !== '.') {
          event.preventDefault();
          return;
        }
      }
    }
  }

  private formatAndSetInputValue(value: any): void {
    const formattedValue = this.formatValue(value);

    this.timeoutId = setTimeout(() => {
      const inputElement = this.el.nativeElement as HTMLInputElement;

      if (!inputElement.isConnected) {
        return;
      }

      const originalValue = inputElement.value;

      if (formattedValue !== inputElement.value) {
        this.renderer.setProperty(inputElement, 'value', formattedValue);

        this.restoreCursorPosition(inputElement, originalValue, formattedValue, inputElement.selectionStart);
      }

      if (this.ngControl && this.ngControl.control) {
        this.ngControl.control.setValue(formattedValue, { emitEvent: false }); // Prevent infinite loops
      }
    });
  }

  private formatPartialValue(value: any): string {
    if (!value || value === '') {
      return '';
    }

    // If it's just a decimal point, return it
    if (value === '.') {
      return value;
    }

    // Remove any existing commas first
    let cleanValue = String(value).replace(/,/g, '');

    // Check if the cleaned value is valid (after removing commas)
    if (isNaN(parseFloat(cleanValue)) && cleanValue !== '.') {
      return '';
    }

    // Handle edge case where value starts with comma (like ",548,965.00")
    // After cleaning, this becomes a valid number
    if (cleanValue === '' || cleanValue === '.') {
      return cleanValue;
    }

    // Split into whole and decimal parts if there's a decimal point
    const parts = cleanValue.split('.');
    const wholeNum = parts[0];
    const decimal = parts[1];

    // If whole number part is empty, handle it gracefully
    if (wholeNum === '') {
      if (decimal !== undefined) {
        return `.${decimal.length > 2 ? decimal.substring(0, 2) : decimal}`;
      }
      return '';
    }

    // Format the whole number part with commas
    const formattedWhole = wholeNum.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    // Return the formatted number
    if (decimal !== undefined) {
      return `${formattedWhole}.${decimal.length > 2 ? decimal.substring(0, 2) : decimal}`;
    }
    return formattedWhole;
  }

  private formatValue(value: any): string {
    if (!value || value === '') {
      return '';
    }

    // Remove any existing commas
    let cleanValue = String(value).replace(/,/g, '');
    
    if (cleanValue === '.' || cleanValue.endsWith('.')) {
      return cleanValue;
    }

    const numericValue = Number(cleanValue);
    if (isNaN(numericValue)) {
      return '';
    }

    // Format with 2 decimal places
    const parts = numericValue.toFixed(2).split('.');
    
    // Format the whole number part with commas
    const formattedWhole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return `${formattedWhole}.${parts[1]}`;
  }

  private restoreCursorPosition(inputElement: HTMLInputElement, originalValue: string, formattedValue: string, cursorPosition: number | null): void {
    if (cursorPosition === null) {
      return;
    }

    // Get the unformatted values (without commas)
    const unformattedOriginal = originalValue.replace(/,/g, '');
    const unformattedFormatted = formattedValue.replace(/,/g, '');
    
    // If the unformatted content is the same, maintain the same relative position
    if (unformattedOriginal === unformattedFormatted) {
      const beforeCursor = originalValue.slice(0, cursorPosition);
      const digitsAndDecimalBeforeCursor = beforeCursor.replace(/,/g, '');
      
      // Find the position in the formatted value that corresponds to the same number of digits/decimal
      let newPosition = 0;
      let digitCount = 0;
      
      for (let i = 0; i < formattedValue.length && digitCount < digitsAndDecimalBeforeCursor.length; i++) {
        if (formattedValue[i] !== ',') {
          digitCount++;
        }
        newPosition = i + 1;
      }
      
      // Set cursor position after a short delay to ensure the input has been updated
      setTimeout(() => {
        if (inputElement.isConnected) {
          inputElement.setSelectionRange(newPosition, newPosition);
        }
      }, 0);
      return;
    }

    // For other cases, use a simpler approach
    const newPosition = Math.min(cursorPosition, formattedValue.length);
    
    setTimeout(() => {
      if (inputElement.isConnected) {
        inputElement.setSelectionRange(newPosition, newPosition);
      }
    }, 0);
  }
}